import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CalendarPlus,
  Check,
  KeyRound,
  Loader2,
  Lock,
  LockOpen,
  MessageCircle,
  Pencil,
  PhoneOff,
  Plus,
  RefreshCw,
  Repeat,
  Search,
  ShieldCheck,
  Trash2,
  TriangleAlert,
  UserCheck,
} from 'lucide-react';
import {
  PLANOS,
  descreverCota,
  formatarPreco,
  formatarTelefone,
  linkWhatsApp,
  listaParaGrupo,
  planoPorId,
  somenteDigitos,
  telefoneValido,
} from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Campo } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, type Convite, type UsuarioDoPainel } from '@/lib/api';
import { cn } from '@/lib/utils';

const ROTULO_ORIGEM: Record<string, string> = {
  INFINITEPAY: 'Comprado',
  MANUAL: 'Liberado por você',
  CORTESIA: 'Cortesia',
  DEMO: 'Demonstração',
};

function quando(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : 'nunca';
}

/** Fim do ciclo sugerido para o plano escolhido, no formato do input de data. */
function fimDoCiclo(dias: number): string {
  const data = new Date(Date.now() + dias * 86_400_000);
  return data.toISOString().slice(0, 10);
}

/**
 * Usuários e tipos de acesso.
 *
 * É aqui que o administrador decide o que cada pessoa pode usar: o plano
 * (essencial, profissional, vendedor, vitalício ou mensal), a validade do ciclo
 * e o bônus da biblioteca de prompts de IA. O mesmo botão libera e bloqueia:
 * com o acesso ativo ele vira “Bloquear”, bloqueado ele volta a ser “Liberar”.
 */
export function UsuariosDoPainel() {
  const [usuarios, setUsuarios] = useState<UsuarioDoPainel[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [trabalhando, setTrabalhando] = useState<string | null>(null);
  const [planoEscolhido, setPlanoEscolhido] = useState<Record<string, string>>({});
  const [telefonesCopiados, setTelefonesCopiados] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const resposta = await api.listarUsuarios(busca.trim() || undefined);
      setUsuarios(resposta.usuarios);
    } catch (falha) {
      setErro(
        falha instanceof Error
          ? falha.message
          : 'Não foi possível carregar os usuários. Entre novamente com a conta de administrador.',
      );
    } finally {
      setCarregando(false);
    }
  }, [busca]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const liberar = async (usuario: UsuarioDoPainel, planoId: string, expiraEm?: string | null) => {
    setTrabalhando(usuario.id);
    try {
      await api.definirAcesso(usuario.id, {
        planoId,
        ativa: true,
        expiraEm: expiraEm ?? null,
        origem: 'MANUAL',
      });
      await carregar();
    } catch {
      setErro('Não foi possível liberar o acesso agora.');
    } finally {
      setTrabalhando(null);
    }
  };

  const bloquear = async (usuario: UsuarioDoPainel) => {
    setTrabalhando(usuario.id);
    try {
      await api.definirAcesso(usuario.id, { ativa: false });
      await carregar();
    } catch {
      setErro('Não foi possível bloquear o acesso agora.');
    } finally {
      setTrabalhando(null);
    }
  };

  const alternarBonus = async (usuario: UsuarioDoPainel) => {
    setTrabalhando(usuario.id);
    try {
      await api.definirBonusPrompts(usuario.id, !usuario.promptsIaLiberado);
      await carregar();
    } catch {
      setErro('Não foi possível alterar o bônus de prompts.');
    } finally {
      setTrabalhando(null);
    }
  };

  /**
   * Dá ou tira o editor de documentos.
   *
   * É liberação nominal: não depende de plano nem de pagamento. Quem perde a
   * liberação **não perde o que escreveu** — os documentos continuam guardados e
   * imprimíveis.
   */
  const alternarEditor = async (usuario: UsuarioDoPainel) => {
    setTrabalhando(usuario.id);
    try {
      await api.definirEditorDeDocumentos(usuario.id, !usuario.documentosLiberado);
      await carregar();
    } catch {
      setErro('Não foi possível alterar a liberação do editor de documentos.');
    } finally {
      setTrabalhando(null);
    }
  };

  /** Liga/desliga o módulo de miolos (a agenda) para a conta. */
  const alternarMiolos = async (usuario: UsuarioDoPainel) => {
    setTrabalhando(usuario.id);
    try {
      await api.definirModuloMiolos(usuario.id, !usuario.moduloMiolos);
      await carregar();
    } catch {
      setErro('Não foi possível alterar o módulo de miolos.');
    } finally {
      setTrabalhando(null);
    }
  };

  /**
   * Liga/desliga o **módulo de textos** — a área exclusiva do editor.
   *
   * É o segundo passo do convite: a pessoa se cadastra normalmente, e sem esta
   * liberação a rota de textos responde 403.
   */
  const alternarTextos = async (usuario: UsuarioDoPainel) => {
    setTrabalhando(usuario.id);
    try {
      await api.definirEditorDeTextos(usuario.id, !usuario.textosLiberado);
      await carregar();
    } catch {
      setErro('Não foi possível alterar o módulo de textos.');
    } finally {
      setTrabalhando(null);
    }
  };

  /**
   * Manda por e-mail o link de redefinição de senha.
   *
   * Serve para o cliente que perdeu a senha e não consegue (ou não sabe) pedir
   * sozinho pela tela "Esqueci minha senha".
   */
  const [avisoSenha, setAvisoSenha] = useState<string | null>(null);
  const enviarRecuperacao = async (usuario: UsuarioDoPainel) => {
    setTrabalhando(usuario.id);
    setAvisoSenha(null);
    try {
      const resultado = await api.enviarRecuperacaoDeSenha(usuario.id);
      setAvisoSenha(
        resultado.entregue
          ? `Link de redefinição enviado para ${usuario.email}.`
          : `O e-mail foi gerado mas não saiu do servidor (guardado na caixa de saída). ${resultado.aviso ?? ''}`.trim(),
      );
    } catch {
      setErro('Não foi possível enviar o link de redefinição de senha.');
    } finally {
      setTrabalhando(null);
    }
  };

  const planos = useMemo(
    () => [...PLANOS].sort((a, b) => a.precoCentavos - b.precoCentavos),
    [],
  );

  /** Quantos clientes da lista têm telefone — é o que dá para chamar hoje. */
  const comTelefone = usuarios.filter((usuario) => usuario.telefone).length;

  /**
   * Conta aberta no editor de contato.
   *
   * O cadastro é do cliente, mas quem digitou errado (ou entrou antes do campo
   * existir) não tem como corrigir sozinho — o painel resolve sem passar pelo
   * suporte.
   */
  const [editando, setEditando] = useState<UsuarioDoPainel | null>(null);
  const [nomeEditado, setNomeEditado] = useState('');
  const [telefoneEditado, setTelefoneEditado] = useState('');
  const [salvandoContato, setSalvandoContato] = useState(false);
  const [erroContato, setErroContato] = useState<string | null>(null);

  const abrirEdicao = (usuario: UsuarioDoPainel) => {
    setEditando(usuario);
    setNomeEditado(usuario.nome);
    setTelefoneEditado(formatarTelefone(usuario.telefone ?? ''));
    setErroContato(null);
  };

  const salvarContato = async (evento: React.FormEvent) => {
    evento.preventDefault();
    if (!editando) return;
    setErroContato(null);

    if (nomeEditado.trim().length < 2) {
      setErroContato('Informe o nome.');
      return;
    }
    if (telefoneEditado.trim() && !telefoneValido(telefoneEditado)) {
      setErroContato('Telefone inválido. Use o formato (11) 91234-5678.');
      return;
    }

    setSalvandoContato(true);
    try {
      await api.editarUsuario(editando.id, {
        nome: nomeEditado.trim(),
        // Campo vazio apaga o número: é assim que se tira o telefone da conta.
        telefone: somenteDigitos(telefoneEditado) ? telefoneEditado : null,
      });
      setEditando(null);
      await carregar();
    } catch (falha) {
      setErroContato(falha instanceof Error ? falha.message : 'Não foi possível salvar agora.');
    } finally {
      setSalvandoContato(false);
    }
  };

  /**
   * O WhatsApp aceita vários números separados por vírgula ao criar um grupo.
   * Copiamos exatamente no formato que ele espera, com o código do país.
   */
  const copiarTelefones = async () => {
    const lista = listaParaGrupo(usuarios.map((usuario) => usuario.telefone));
    if (!lista) return;
    try {
      await navigator.clipboard.writeText(lista);
      setTelefonesCopiados(true);
      window.setTimeout(() => setTelefonesCopiados(false), 2500);
    } catch {
      setErro('Não foi possível copiar a lista de telefones.');
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <KeyRound className="size-4" />
          Usuários e tipos de acesso
        </h2>
        <Badge tom="neutro">{usuarios.length} conta(s)</Badge>
        <div className="relative ml-auto w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
            placeholder="Buscar por nome, e-mail ou telefone"
            className="h-9 w-full rounded-md border border-border bg-background pl-8 pr-3 text-sm"
          />
        </div>
        <Button variant="contorno" tamanho="pequeno" onClick={carregar} disabled={carregando}>
          {carregando ? <Loader2 className="animate-spin" /> : <RefreshCw />}
          Atualizar
        </Button>
        <Button
          variant="contorno"
          tamanho="pequeno"
          onClick={copiarTelefones}
          disabled={comTelefone === 0}
          title={
            comTelefone === 0
              ? 'Nenhuma conta desta lista tem telefone cadastrado.'
              : 'Copia os números no formato que o WhatsApp usa para criar um grupo.'
          }
        >
          {telefonesCopiados ? <Check /> : <MessageCircle />}
          {telefonesCopiados ? 'Telefones copiados' : `Telefones para grupo (${comTelefone})`}
        </Button>
      </div>

      <p className="rounded-md border border-border bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">
        Escolha o plano e clique no botão: com o acesso bloqueado ele aparece como
        <strong className="text-foreground"> Liberar</strong>; depois de liberado, o mesmo botão vira
        <strong className="text-foreground"> Bloquear</strong> e tira o acesso na hora (a conta continua existindo).
        Use <strong className="text-foreground">7 dias</strong> para teste e
        <strong className="text-foreground"> Trocar plano</strong> para renovar ou mudar o pacote de quem já tem acesso.
        O acesso de demonstração (plano “Miolo de teste”) imprime a marca de cópia não autorizada.
      </p>

      {erro ? (
        <p className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          {erro}
        </p>
      ) : null}

      {avisoSenha ? (
        <p className="rounded-lg border border-primary/40 bg-primary/5 p-4 text-sm text-foreground">{avisoSenha}</p>
      ) : null}

      {carregando && usuarios.length === 0 ? (
        <p className="flex items-center justify-center gap-2 rounded-lg border border-border p-8 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Carregando contas...
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-xs">
            <thead className="bg-muted/60 text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-semibold">Conta</th>
                <th className="px-3 py-2 font-semibold">Acesso atual</th>
                <th className="px-3 py-2 font-semibold">Acesso e bloqueio</th>
                <th className="px-3 py-2 font-semibold">Bônus</th>
                <th className="px-3 py-2 font-semibold">Uso</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map((usuario) => {
                const plano = usuario.plano ? planoPorId(usuario.plano) : null;
                const escolhido = planoEscolhido[usuario.id] ?? usuario.plano ?? 'essencial';
                const ehMensal = planoPorId(escolhido)?.cobranca === 'MENSAL';

                return (
                  <tr key={usuario.id} className="border-t border-border/70 align-top">
                    <td className="px-3 py-3">
                      <span className="block font-medium text-foreground">
                        {usuario.nome}
                        {usuario.admin ? <Badge className="ml-1 text-[10px]">administrador</Badge> : null}
                      </span>
                      <span className="text-muted-foreground">{usuario.email}</span>
                      {usuario.telefone ? (
                        <a
                          className="mt-0.5 flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                          href={linkWhatsApp(usuario.telefone) ?? '#'}
                          target="_blank"
                          rel="noreferrer"
                          title="Abrir conversa no WhatsApp"
                        >
                          <MessageCircle className="size-3" />
                          {formatarTelefone(usuario.telefone)}
                        </a>
                      ) : (
                        <span className="mt-0.5 flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-400">
                          <PhoneOff className="size-3" />
                          sem telefone
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => abrirEdicao(usuario)}
                        className="mt-1 flex items-center gap-1 text-[11px] font-medium text-muted-foreground transition-colors hover:text-primary"
                        title="Corrigir o nome e o telefone desta conta"
                      >
                        <Pencil className="size-3" />
                        Editar contato
                      </button>
                      <span className="mt-1 block text-[11px] text-muted-foreground">
                        desde {quando(usuario.criadoEm)} · último acesso {quando(usuario.ultimoAcessoEm)}
                      </span>
                    </td>

                    <td className="px-3 py-3">
                      {usuario.plano ? (
                        <span className="flex flex-wrap items-center gap-1.5">
                          <Badge tom={usuario.licencaAtiva ? 'principal' : 'neutro'}>
                            {plano ? plano.nome : usuario.plano}
                          </Badge>
                          <Badge tom="neutro" className="text-[10px]">
                            {usuario.licencaAtiva ? 'ativo' : <><LockOpen className="size-3" /> bloqueado</>}
                          </Badge>
                        </span>
                      ) : (
                        <Badge tom="neutro">sem acesso</Badge>
                      )}
                    </td>

                    <td className="px-3 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <Select
                          value={escolhido}
                          onValueChange={(valor) =>
                            setPlanoEscolhido((atual) => ({ ...atual, [usuario.id]: valor }))
                          }
                        >
                          <SelectTrigger className="h-8 w-[210px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {planos.map((item) => (
                              <SelectItem key={item.id} value={item.id}>
                                {item.nome} · {formatarPreco(item.precoCentavos)}
                                {item.cobranca === 'MENSAL' ? '/mês' : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Button
                          tamanho="pequeno"
                          variant="contorno"
                          onClick={() => liberar(usuario, escolhido, fimDoCiclo(7))}
                          disabled={trabalhando === usuario.id}
                        >
                          <ShieldCheck />
                          7 dias
                        </Button>

                        {usuario.licencaAtiva ? (
                          <>
                            {ehMensal ? (
                              <Button
                                tamanho="pequeno"
                                variant="contorno"
                                onClick={() => liberar(usuario, escolhido, fimDoCiclo(30))}
                                disabled={trabalhando === usuario.id}
                                title="Empurra o fim do ciclo para daqui a 30 dias."
                              >
                                <CalendarPlus />
                                Renovar
                              </Button>
                            ) : null}
                            {escolhido !== usuario.plano ? (
                              <Button
                                tamanho="pequeno"
                                variant="contorno"
                                onClick={() => liberar(usuario, escolhido, ehMensal ? fimDoCiclo(30) : null)}
                                disabled={trabalhando === usuario.id}
                              >
                                <Repeat />
                                Trocar plano
                              </Button>
                            ) : null}
                            <Button
                              tamanho="pequeno"
                              variant="destrutivo"
                              onClick={() => bloquear(usuario)}
                              disabled={trabalhando === usuario.id || usuario.admin}
                              title={
                                usuario.admin ? 'O administrador não pode bloquear o próprio acesso.' : undefined
                              }
                            >
                              {trabalhando === usuario.id ? <Loader2 className="animate-spin" /> : <Lock />}
                              Bloquear
                            </Button>
                          </>
                        ) : (
                          <Button
                            tamanho="pequeno"
                            onClick={() => liberar(usuario, escolhido, ehMensal ? fimDoCiclo(30) : null)}
                            disabled={trabalhando === usuario.id}
                          >
                            {trabalhando === usuario.id ? <Loader2 className="animate-spin" /> : <UserCheck />}
                            Liberar
                          </Button>
                        )}
                      </div>

                      <span className="mt-1.5 block text-[11px] text-muted-foreground">
                        {usuario.admin
                          ? 'Administrador: acesso permanente, sem risco de bloqueio.'
                          : usuario.licencaAtiva
                            ? ehMensal
                              ? 'Plano mensal: renove definindo a data ou liberando de novo (30 dias).'
                              : plano
                                ? `Limite: ${plano.limites.miolos === 0 ? 'sem limite de miolos' : `${plano.limites.miolos} miolos`} · ${descreverCota(plano)}`
                                : 'Sem prazo de validade.'
                            : `${usuario.plano ? 'Bloqueado' : 'Sem acesso'} — clique em Liberar para devolver o acesso com o plano escolhido.`}
                      </span>
                    </td>

                    <td className="px-3 py-3">
                      <button
                        type="button"
                        onClick={() => alternarBonus(usuario)}
                        disabled={usuario.admin || trabalhando === usuario.id}
                        className={cn(
                          'rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors',
                          usuario.promptsIA
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border text-muted-foreground hover:border-primary/40',
                          usuario.admin ? 'cursor-not-allowed opacity-70' : '',
                        )}
                      >
                        {usuario.promptsIA ? 'Prompts de IA: liberado' : 'Dar bônus de prompts'}
                      </button>
                      <span className="mt-1 block text-[11px] text-muted-foreground">
                        {usuario.admin ? 'Administrador já tem acesso a tudo.' : `${usuario.totalPrompts} prompt(s) salvos`}
                      </span>

                      <button
                        type="button"
                        onClick={() => alternarEditor(usuario)}
                        disabled={usuario.admin || trabalhando === usuario.id}
                        className={cn(
                          'mt-2 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors',
                          usuario.editorDeDocumentos
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border text-muted-foreground hover:border-primary/40',
                          usuario.admin ? 'cursor-not-allowed opacity-70' : '',
                        )}
                      >
                        {usuario.editorDeDocumentos
                          ? 'Editor de documentos: liberado'
                          : 'Liberar editor de documentos'}
                      </button>

                      <button
                        type="button"
                        onClick={() => alternarMiolos(usuario)}
                        disabled={usuario.admin || trabalhando === usuario.id}
                        className={cn(
                          'mt-2 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors',
                          usuario.moduloMiolos
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border text-muted-foreground hover:border-primary/40',
                          usuario.admin ? 'cursor-not-allowed opacity-70' : '',
                        )}
                      >
                        {usuario.moduloMiolos ? 'Módulo miolos: ativo' : 'Ativar módulo miolos'}
                      </button>

                      <button
                        type="button"
                        onClick={() => alternarTextos(usuario)}
                        disabled={usuario.admin || trabalhando === usuario.id}
                        className={cn(
                          'mt-2 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors',
                          usuario.editorDeTextos
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border text-muted-foreground hover:border-primary/40',
                          usuario.admin ? 'cursor-not-allowed opacity-70' : '',
                        )}
                      >
                        {usuario.editorDeTextos ? 'Módulo de textos: liberado' : 'Liberar módulo de textos'}
                      </button>

                      <button
                        type="button"
                        onClick={() => enviarRecuperacao(usuario)}
                        disabled={trabalhando === usuario.id}
                        className="mt-2 block rounded-full border border-border px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
                      >
                        Enviar link de redefinição de senha
                      </button>
                    </td>

                    <td className="px-3 py-3 text-muted-foreground">
                      <span className="block">{usuario.totalMiolos} miolo(s)</span>
                      {/*
                        O download é o que se cobra: um miolo baixado, um miolo
                        usado do plano. É este número que o administrador olha
                        quando a cota do plano acaba.
                      */}
                      <span className="block text-[11px]">{usuario.totalDownloads} download(s)</span>
                      {usuario.licencaOrigem ? (
                        <span className="block text-[11px]">
                          {ROTULO_ORIGEM[usuario.licencaOrigem] ?? usuario.licencaOrigem}
                          {usuario.licencaExpiraEm
                            ? ` · até ${new Date(usuario.licencaExpiraEm).toLocaleDateString('pt-BR')}`
                            : ''}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Editor de contato: nome e telefone, sem mexer em plano nem em senha. */}
      <Dialog open={editando !== null} onOpenChange={(aberto) => (aberto ? null : setEditando(null))}>
        <DialogContent
          titulo="Editar contato"
          descricao={
            editando
              ? `${editando.email} — o e-mail de acesso e a senha não mudam por aqui.`
              : undefined
          }
        >
          <form className="space-y-4" onSubmit={salvarContato}>
            <Campo rotulo="Nome">
              <Input
                required
                value={nomeEditado}
                onChange={(evento) => setNomeEditado(evento.target.value)}
                placeholder="Como devo chamar esta pessoa"
              />
            </Campo>

            <Campo rotulo="Telefone / WhatsApp" dica="Com DDD. Deixe em branco para remover o número.">
              <Input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                maxLength={15}
                value={telefoneEditado}
                onChange={(evento) => setTelefoneEditado(formatarTelefone(evento.target.value))}
                placeholder="(11) 91234-5678"
              />
            </Campo>

            {erroContato ? <p className="text-xs text-destructive">{erroContato}</p> : null}

            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button
                type="button"
                variant="fantasma"
                onClick={() => setEditando(null)}
                disabled={salvandoContato}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={salvandoContato}>
                {salvandoContato ? <Loader2 className="animate-spin" /> : <Check />}
                Salvar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConvitesDoPainel />

      <ContatosDoPainel />

      <ManutencaoDoPainel />

      <ConfiguracoesDoPainel />
    </section>
  );
}

/** Configurações do sistema (a tag do InfinitePay, por exemplo). */
function ConfiguracoesDoPainel() {
  const [tag, setTag] = useState('');
  const [tagEmUso, setTagEmUso] = useState('');
  const [carregando, setCarregando] = useState(false);

  const carregar = async () => {
    try {
      const config = await api.configuracoesDoAdmin();
      setTag(config.tagDoInfinitePay);
      setTagEmUso(config.tagEmUso);
    } catch {
      /* Silencioso. */
    }
  };

  useEffect(() => {
    void carregar();
  }, []);

  const salvar = async () => {
    setCarregando(true);
    try {
      const config = await api.salvarTagDoInfinitePay(tag);
      setTag(config.tagDoInfinitePay);
      setTagEmUso(config.tagEmUso);
    } catch {
      /* Silencioso. */
    } finally {
      setCarregando(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Configurações</CardTitle>
        <CardDescription>
          A tag (handle) do InfinitePay usada no checkout. Deixe em branco para voltar à tag do
          ambiente (.env).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Campo rotulo="Tag do InfinitePay" dica={`Em uso agora: ${tagEmUso || '—'}`}>
          <Input
            value={tag}
            onChange={(evento) => setTag(evento.target.value)}
            placeholder={tagEmUso || 'hidalgojunior'}
          />
        </Campo>
        <Button tamanho="pequeno" onClick={() => void salvar()} disabled={carregando}>
          Salvar tag
        </Button>
      </CardContent>
    </Card>
  );
}

/** Liga/desliga o modo manutenção do site. */
function ManutencaoDoPainel() {
  const [ativa, setAtiva] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [carregando, setCarregando] = useState(false);

  const carregar = async () => {
    try {
      const estado = await api.estadoDaManutencao();
      setAtiva(estado.ativa);
      setMensagem(estado.mensagem);
    } catch {
      /* Silencioso. */
    }
  };

  useEffect(() => {
    void carregar();
  }, []);

  const salvar = async (novo: boolean) => {
    setCarregando(true);
    try {
      const estado = await api.salvarManutencao({ ativa: novo, mensagem });
      setAtiva(estado.ativa);
      setMensagem(estado.mensagem);
    } catch {
      /* Silencioso. */
    } finally {
      setCarregando(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Modo manutenção</CardTitle>
        <CardDescription>
          Quando ligado, o site mostra uma página de manutenção para todo mundo — menos para você
          (administrador), que continua entrando para desligar.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Campo rotulo="Mensagem" dica="O que os visitantes veem enquanto o site está em manutenção.">
          <Input
            value={mensagem}
            onChange={(evento) => setMensagem(evento.target.value)}
            placeholder="Estamos fazendo melhorias. Volte em alguns minutos."
          />
        </Campo>
        <div className="flex flex-wrap gap-2">
          <Button
            tamanho="pequeno"
            variant={ativa ? 'destrutivo' : 'contorno'}
            onClick={() => void salvar(!ativa)}
            disabled={carregando}
          >
            {ativa ? 'Desligar manutenção' : 'Ligar manutenção'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** Mensagens de contato com o desenvolvedor. */
function ContatosDoPainel() {
  const [contatos, setContatos] = useState<
    Array<{ id: string; nome: string; email: string; mensagem: string; criadoEm: string; lido: boolean }>
  >([]);

  const carregar = async () => {
    try {
      setContatos((await api.listarContatos()).contatos);
    } catch {
      /* Silencioso: a lista é um extra, o e-mail é o canal principal. */
    }
  };

  useEffect(() => {
    void carregar();
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Contato com o desenvolvedor</CardTitle>
        <CardDescription>Mensagens de quem usa a plataforma — sugestões e pedidos.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {contatos.length > 0 ? (
          <ul className="space-y-2">
            {contatos.map((contato) => (
              <li key={contato.id} className="rounded-md border border-border p-2 text-sm">
                <p>
                  <strong>{contato.nome}</strong> · <span className="text-muted-foreground">{contato.email}</span>
                  <span className="ml-1 text-[11px] text-muted-foreground">
                    · {new Date(contato.criadoEm).toLocaleDateString('pt-BR')}
                  </span>
                </p>
                <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{contato.mensagem}</p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-muted-foreground">Nenhuma mensagem ainda.</p>
        )}
      </CardContent>
    </Card>
  );
}

/** Convites de teste por módulo: miolos ou documentos, cada um libera só o seu. */
function ConvitesDoPainel() {
  const [convites, setConvites] = useState<Convite[]>([]);
  const [erro, setErro] = useState('');

  const carregar = async () => {
    try {
      setConvites((await api.listarConvites()).convites);
    } catch {
      setErro('Não foi possível carregar os convites.');
    }
  };

  useEffect(() => {
    void carregar();
  }, []);

  const criar = async (modulo: 'MIOLOS' | 'DOCUMENTOS' | 'TEXTOS') => {
    try {
      await api.criarConvite(modulo);
      await carregar();
    } catch {
      setErro('Não foi possível criar o convite.');
    }
  };

  const excluir = async (id: string) => {
    try {
      await api.excluirConvite(id);
      await carregar();
    } catch {
      setErro('Não foi possível apagar o convite.');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Convites de teste</CardTitle>
        <CardDescription>
          Cada convite libera **um** módulo para quem abrir o link — agenda, editor acadêmico ou editor de
          textos, em separado.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button tamanho="pequeno" onClick={() => void criar('MIOLOS')}>
            <Plus />
            Convite para Miolos
          </Button>
          <Button tamanho="pequeno" onClick={() => void criar('DOCUMENTOS')}>
            <Plus />
            Convite para Documentos
          </Button>
          <Button tamanho="pequeno" onClick={() => void criar('TEXTOS')}>
            <Plus />
            Convite para Textos
          </Button>
        </div>

        {erro ? <p className="text-xs text-destructive">{erro}</p> : null}

        {convites.length > 0 ? (
          <ul className="space-y-2">
            {convites.map((convite) => (
              <li
                key={convite.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-2"
              >
                <span className="min-w-0 text-sm">
                  <strong>{convite.modulo === 'MIOLOS' ? 'Miolos' : convite.modulo === 'TEXTOS' ? 'Textos' : 'Documentos'}</strong>
                  {convite.usadoEm ? (
                    <span className="text-muted-foreground"> · usado</span>
                  ) : (
                    <span className="text-success"> · disponível</span>
                  )}
                  <span className="ml-2 block font-mono text-xs text-muted-foreground">
                    {window.location.origin}/convidar/{convite.codigo}
                  </span>
                </span>
                <Button variant="contorno" tamanho="pequeno" onClick={() => void excluir(convite.id)}>
                  <Trash2 />
                  Apagar
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-muted-foreground">Nenhum convite ainda.</p>
        )}
      </CardContent>
    </Card>
  );
}
